import { useState, useEffect } from 'react'

/**
 * Custom hook for persisting state in localStorage
 * @param key - localStorage key
 * @param initialValue - initial value if nothing in localStorage
 * @returns [value, setValue] tuple
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue
    }
    
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      
      // Save state
      setStoredValue(valueToStore)
      
      // Save to localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error)
    }
  }

  return [storedValue, setValue]
}

/**
 * Hook for persisting Set objects in localStorage
 * Specifically designed for hierarchical table expansion states
 */
export function useLocalStorageSet(key: string, initialValue: Set<string> = new Set()): [Set<string>, (value: Set<string> | ((val: Set<string>) => Set<string>)) => void] {
  const [storedArray, setStoredArray] = useLocalStorage<string[]>(key, Array.from(initialValue))
  
  const [storedSet, setStoredSet] = useState<Set<string>>(() => new Set(storedArray))
  
  // Update set when stored array changes
  useEffect(() => {
    setStoredSet(new Set(storedArray))
  }, [storedArray])
  
  const setValue = (value: Set<string> | ((val: Set<string>) => Set<string>)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedSet) : value
      const arrayToStore = Array.from(valueToStore)
      setStoredArray(arrayToStore)
      setStoredSet(valueToStore)
    } catch (error) {
      console.warn(`Error setting localStorage Set for key "${key}":`, error)
    }
  }
  
  return [storedSet, setValue]
}

export default useLocalStorage